import { FC, useEffect, useState } from 'react';

import {
  Box,
  Fade,
  Flex,
  Heading,
  HStack,
  SlideFade,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';

import { Link } from '@/components/common';
import { Button } from '@/components/core';
import { FONTS_MORE_OPACITY, FONTS_OPACITY, ROUTES } from '@/constant';

import {
  PAGE_STATE_CONFIGS,
  PAGE_STATE_HEADER_BG,
  PAGE_STATE_HOME_BUTTON_VARIANTS,
  PAGE_STATE_LOADING_VIDEO_SRC,
  PAGE_STATE_LOGO_SRC,
  PAGE_STATE_OVERLAY_VARIANTS,
  PAGE_STATE_TITLE_GRADIENT,
  type PageStateVariant,
} from './constants';

interface PageStateProps {
  variant?: PageStateVariant;
}

export const PageState: FC<PageStateProps> = ({ variant = 'empty' }) => {
  const config = PAGE_STATE_CONFIGS[variant];
  const isOverlayState = PAGE_STATE_OVERLAY_VARIANTS.includes(variant);
  const isLoadingState = variant === 'reconnecting';
  const isMaintenanceState = variant === 'maintenance';
  const isErrorState = variant === 'error';
  const isShowHomeButton = PAGE_STATE_HOME_BUTTON_VARIANTS.includes(variant);
  const isShowActionButton = isShowHomeButton || isErrorState;
  const [isMediaLoaded, setIsMediaLoaded] = useState(false);

  useEffect(() => {
    setIsMediaLoaded(false);
  }, [variant]);

  return (
    <Fade
      in
      key={`page-state-${variant}`}
      transition={{ enter: { duration: 0.55 }, exit: { duration: 0.25 } }}
    >
      <Box
        position={isErrorState ? 'relative' : 'fixed'}
        zIndex={isOverlayState ? 1 : 0}
        inset={isErrorState ? 'auto' : 0}
        width='full'
        height={
          isErrorState
            ? { base: 'calc(100dvh - 3rem)', md: 'calc(100dvh - 4rem)' }
            : 'full'
        }
        display='flex'
        alignItems='center'
        justifyContent='center'
        overflow='hidden'
        pointerEvents={isOverlayState ? 'auto' : 'none'}
        bg='empty-state-background'
        data-cy={`page-state-${variant}`}
      >
        {isOverlayState && (
          <Stack
            as='header'
            position='absolute'
            top={0}
            left={0}
            right={0}
            zIndex={2}
            bg={PAGE_STATE_HEADER_BG}
            backdropFilter='12px'
          >
            <HStack
              borderBottomWidth='1px'
              py={4}
              px={{ base: 4, xl: 6 }}
              justifyContent='space-between'
              alignItems='center'
            >
              <Flex
                width={{ base: 126, md: 140 }}
                alignItems='center'
                justifyContent='flex-start'
              >
                <Box
                  as='img'
                  src={PAGE_STATE_LOGO_SRC}
                  alt='VH3 AI'
                  width='100%'
                  height='100%'
                  objectFit='contain'
                />
              </Flex>
              <Text size='secondaryRegular' color='white'>
                {config.topBarTitle}
              </Text>
            </HStack>
          </Stack>
        )}

        <Box
          position='absolute'
          top={0}
          left={0}
          right={0}
          bottom={0}
          zIndex={1}
          overflow='hidden'
          display='flex'
          alignItems='center'
          justifyContent='center'
          pointerEvents={isOverlayState ? 'auto' : 'none'}
        >
          {isLoadingState ? (
            <Box
              key={`${variant}-media`}
              as='video'
              src={PAGE_STATE_LOADING_VIDEO_SRC}
              autoPlay
              loop
              muted
              playsInline
              preload='metadata'
              onLoadedData={() => setIsMediaLoaded(true)}
              onCanPlay={() => setIsMediaLoaded(true)}
              h='full'
              w='auto'
              minW='940px'
              minH='940px'
              maxW='none'
              maxH='none'
              transform={{ base: 'scale(0.6)', lg: 'none' }}
              transformOrigin='center'
              opacity={isMediaLoaded ? 1 : 0}
              transition='opacity 800ms ease-out'
            />
          ) : (
            <Box
              key={`${variant}-media`}
              as='img'
              src={config.imageSrc}
              alt={`${variant} state`}
              loading='lazy'
              decoding='async'
              fetchPriority='low'
              onLoad={() => setIsMediaLoaded(true)}
              onError={() => setIsMediaLoaded(true)}
              h='full'
              w='auto'
              minW={isErrorState ? '2200px' : '2560px'}
              minH={isErrorState ? '1318px' : '1534px'}
              maxW='none'
              maxH='none'
              transform={{ base: 'scale(0.55)', lg: 'none' }}
              transformOrigin='center'
              opacity={isMediaLoaded ? 1 : 0}
              transition='opacity 800ms ease-out'
            />
          )}

          <Box
            position='absolute'
            inset={0}
            display='flex'
            alignItems='center'
            justifyContent='center'
            zIndex={1}
            pointerEvents='auto'
          >
            <SlideFade
              in
              key={`page-state-content-${variant}`}
              offsetY='8px'
              transition={{ enter: { duration: 0.5 }, exit: { duration: 0.2 } }}
            >
              <VStack
                spacing={0}
                textAlign='center'
                width='full'
                px={6}
                mt={isMaintenanceState ? { base: '3.75rem', lg: 0 } : 0}
              >
                <Heading
                  data-cy={`page-state-${variant}-title`}
                  fontSize={{ base: '2.25rem', lg: '3.5rem' }}
                  lineHeight={{ base: '2.5rem', lg: '4rem' }}
                  fontWeight={600}
                  bgGradient={PAGE_STATE_TITLE_GRADIENT}
                  bgClip='text'
                  sx={{
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  {config.title}
                </Heading>
                <Text
                  size='secondaryRegular'
                  color='white'
                  opacity={FONTS_OPACITY}
                  mt={2}
                  mb={8}
                  whiteSpace='pre-line'
                >
                  {config.description}
                </Text>
                {isShowActionButton &&
                  (isErrorState ? (
                    <Button
                      data-cy='page-state-error-action'
                      variant='glassy'
                      onClick={() => window.location.reload()}
                    >
                      Refresh page
                    </Button>
                  ) : (
                    <Link to={ROUTES.DASHBOARD}>
                      <Button variant='glassy'>Back to dashboard</Button>
                    </Link>
                  ))}
                <Box>
                  {config.footerPrimary && (
                    <Text size='secondaryRegular' color='white' mb={1}>
                      {config.footerPrimary}
                    </Text>
                  )}
                  {config.footer && (
                    <Text
                      size='additionalRegular'
                      opacity={FONTS_MORE_OPACITY}
                      whiteSpace='pre-line'
                    >
                      {config.footer}
                    </Text>
                  )}
                </Box>
              </VStack>
            </SlideFade>
          </Box>
        </Box>
      </Box>
    </Fade>
  );
};

export type { PageStateVariant };
